<template>
  <div class="order-page">
    <!-- 顶部状态看板：横向大数字卡片 -->
    <div class="status-board">
      <div v-for="(item, index) in statusCards" :key="index" class="status-item" :class="item.class">
        <div class="status-num">{{ item.value }}</div>
        <div class="status-name">{{ item.label }}</div>
        <div class="status-bar">
          <div class="status-fill" :style="{ width: item.percent + '%' }"></div>
        </div>
      </div>
    </div>

    <!-- 主体三栏布局 -->
    <div class="main-layout">
      <!-- 左侧：待处理订单 -->
      <div class="panel left-panel">
        <div class="panel-header">
          <span class="panel-title">◈ 待处理订单</span>
          <span class="panel-badge">{{ pendingOrders.length }}</span>
        </div>
        <div class="panel-body scroll-area">
          <div v-for="(order, idx) in pendingOrders" :key="idx" class="pending-card" :class="order.urgency">
            <div class="pending-top">
              <span class="pending-code">{{ order.code }}</span>
              <span class="pending-time">{{ order.time }}</span>
            </div>
            <div class="pending-supplier">{{ order.supplier }}</div>
            <div class="pending-goods">{{ order.goods }}</div>
            <div class="pending-bottom">
              <span class="pending-amount">{{ order.amount }}</span>
              <button class="pending-btn" @click="handleProcess(order)">处理</button>
            </div>
          </div>
        </div>
      </div>

      <!-- 中间：订单处理时间轴 -->
      <div class="panel center-panel">
        <div class="panel-header">
          <span class="panel-title">◉ 今日订单流转</span>
          <span class="live-dot"></span>
        </div>
        <div class="panel-body scroll-area">
          <div class="timeline-wrap">
            <div v-for="(item, idx) in timelineList" :key="idx" class="timeline-item" :class="item.type">
              <div class="timeline-dot"></div>
              <div class="timeline-content">
                <div class="timeline-title">{{ item.title }}</div>
                <div class="timeline-desc">{{ item.desc }}</div>
                <div class="timeline-meta">
                  <span>{{ item.supplier }}</span>
                  <span>{{ item.time }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧：交付排行 + 图表 -->
      <div class="panel right-panel">
        <div class="panel-header">
          <span class="panel-title">◆ 供应商交付排行</span>
        </div>
        <div class="panel-body">
          <div ref="chartDelivery" class="chart-area"></div>
          <div class="rank-list">
            <div v-for="(item, idx) in deliveryRank" :key="idx" class="rank-item">
              <span class="rank-num" :class="{ top: idx < 3 }">{{ idx + 1 }}</span>
              <span class="rank-name">{{ item.name }}</span>
              <div class="rank-bar">
                <div class="rank-fill" :style="{ width: item.score + '%' }"></div>
              </div>
              <span class="rank-score">{{ item.score }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部：订单明细表格 -->
    <div class="bottom-table">
      <div class="table-header">
        <span class="table-title">◊ 订单明细</span>
        <div class="filter-group">
          <select v-model="filterStatus" class="filter-select">
            <option value="">全部状态</option>
            <option value="pending">待确认</option>
            <option value="confirmed">已确认</option>
            <option value="shipped">已发货</option>
            <option value="received">已收货</option>
            <option value="settled">已结算</option>
          </select>
          <button class="action-btn" @click="handleExport">导出</button>
        </div>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>订单编号</th>
              <th>供应商</th>
              <th>采购物品</th>
              <th>数量</th>
              <th>金额</th>
              <th>下单时间</th>
              <th>要求交期</th>
              <th>当前状态</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, idx) in filteredOrders" :key="idx">
              <td class="code-cell">{{ item.code }}</td>
              <td>{{ item.supplier }}</td>
              <td class="goods-cell">{{ item.goods }}</td>
              <td>{{ item.qty }}</td>
              <td class="amount-cell">{{ item.amount }}</td>
              <td>{{ item.orderTime }}</td>
              <td :class="{ urgent: item.isUrgent }">{{ item.deadline }}</td>
              <td>
                <div class="status-step">
                  <span v-for="(s, i) in statusSteps" :key="i" class="step-dot" :class="{ 
                    done: i < statusIndex(item.status), 
                    current: i === statusIndex(item.status) 
                  }"></span>
                </div>
                <span class="status-text">{{ statusText(item.status) }}</span>
              </td>
              <td>
                <button class="op-btn primary" @click="handleTrack(item)">跟踪</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="table-footer">
        <span class="footer-info">共 {{ filteredOrders.length }} 条记录</span>
        <div class="pagination">
          <button class="page-btn" :disabled="currentPage === 1" @click="currentPage--">←</button>
          <span class="page-num" v-for="p in pageList" :key="p" :class="{ active: p === currentPage }" @click="currentPage = p">{{ p }}</span>
          <button class="page-btn" :disabled="currentPage === totalPage" @click="currentPage++">→</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SrmOrder',
  data() {
    return {
      statusCards: [
        { label: '今日新增', value: '42', percent: 85, class: 'blue' },
        { label: '待确认', value: '18', percent: 42, class: 'orange' },
        { label: '已发货', value: '26', percent: 62, class: 'cyan' },
        { label: '已收货', value: '15', percent: 36, class: 'green' },
        { label: '异常订单', value: '3', percent: 7, class: 'red' }
      ],
      pendingOrders: [
        { code: 'PO-20260625-0092', supplier: '深圳华强电子', goods: 'IC芯片 STM32F407VGT6 × 5,000pcs', amount: '¥86万', time: '10分钟前', urgency: 'urgent' },
        { code: 'PO-20260625-0091', supplier: '苏州精密制造', goods: '铝合金外壳加工件 × 2,000件', amount: '¥32万', time: '25分钟前', urgency: 'normal' },
        { code: 'PO-20260625-0090', supplier: '青岛海润化工', goods: '环氧树脂涂料 × 10吨', amount: '¥18万', time: '40分钟前', urgency: 'normal' },
        { code: 'PO-20260625-0089', supplier: '杭州半导体', goods: '晶圆片 8英寸 × 200片', amount: '¥120万', time: '1小时前', urgency: 'urgent' },
        { code: 'PO-20260625-0088', supplier: '东莞金属制品', goods: '不锈钢紧固件 M6×20 × 50,000pcs', amount: '¥5.6万', time: '2小时前', urgency: 'low' },
        { code: 'PO-20260625-0087', supplier: '武汉光谷光电', goods: '激光模组 500mW × 300pcs', amount: '¥45万', time: '3小时前', urgency: 'normal' }
      ],
      timelineList: [
        { title: '订单已创建', desc: '采购订单 #PO-20260625-0092 已下发至供应商', supplier: '深圳华强电子', time: '11:30', type: 'create' },
        { title: '供应商确认', desc: '苏州精密制造 已确认订单，预计7月2日交付', supplier: '苏州精密制造', time: '11:25', type: 'confirm' },
        { title: '货物已发出', desc: '青岛海润化工 已发货，物流单号：SF20260625001', supplier: '青岛海润化工', time: '11:15', type: 'ship' },
        { title: '订单已收货', desc: '杭州半导体 交付的晶圆片已质检入库', supplier: '杭州半导体', time: '10:50', type: 'receive' },
        { title: '付款已结算', desc: '东莞金属制品 订单款项已支付', supplier: '东莞金属制品', time: '10:30', type: 'settle' },
        { title: '异常预警', desc: '武汉光谷光电 交付延迟，预计延期3天', supplier: '武汉光谷光电', time: '10:15', type: 'warn' },
        { title: '订单已创建', desc: '采购订单 #PO-20260625-0091 已下发至供应商', supplier: '苏州精密制造', time: '09:45', type: 'create' },
        { title: '供应商确认', desc: '深圳华强电子 已确认订单，预计6月30日交付', supplier: '深圳华强电子', time: '09:30', type: 'confirm' }
      ],
      deliveryRank: [
        { name: '深圳华强电子', score: 98 },
        { name: '苏州精密制造', score: 96 },
        { name: '杭州半导体', score: 94 },
        { name: '青岛海润化工', score: 91 },
        { name: '北京自动化设备', score: 88 },
        { name: '东莞金属制品', score: 85 },
        { name: '武汉光谷光电', score: 82 },
        { name: '成都西部智能', score: 79 }
      ],
      orderList: [
        { code: 'PO-20260625-0092', supplier: '深圳华强电子', goods: 'IC芯片 STM32F407VGT6', qty: '5,000pcs', amount: '¥86万', orderTime: '2026-06-25 11:30', deadline: '2026-06-30', status: 'pending', isUrgent: true },
        { code: 'PO-20260625-0091', supplier: '苏州精密制造', goods: '铝合金外壳加工件', qty: '2,000件', amount: '¥32万', orderTime: '2026-06-25 10:45', deadline: '2026-07-05', status: 'confirmed', isUrgent: false },
        { code: 'PO-20260625-0090', supplier: '青岛海润化工', goods: '环氧树脂涂料', qty: '10吨', amount: '¥18万', orderTime: '2026-06-25 09:20', deadline: '2026-07-02', status: 'shipped', isUrgent: false },
        { code: 'PO-20260625-0089', supplier: '杭州半导体', goods: '晶圆片 8英寸', qty: '200片', amount: '¥120万', orderTime: '2026-06-24 16:00', deadline: '2026-06-28', status: 'received', isUrgent: true },
        { code: 'PO-20260625-0088', supplier: '东莞金属制品', goods: '不锈钢紧固件 M6×20', qty: '50,000pcs', amount: '¥5.6万', orderTime: '2026-06-24 14:30', deadline: '2026-07-10', status: 'settled', isUrgent: false },
        { code: 'PO-20260625-0087', supplier: '武汉光谷光电', goods: '激光模组 500mW', qty: '300pcs', amount: '¥45万', orderTime: '2026-06-24 11:00', deadline: '2026-06-27', status: 'shipped', isUrgent: true },
        { code: 'PO-20260625-0086', supplier: '北京自动化设备', goods: '伺服电机 750W', qty: '100台', amount: '¥68万', orderTime: '2026-06-24 09:15', deadline: '2026-07-08', status: 'confirmed', isUrgent: false },
        { code: 'PO-20260625-0085', supplier: '成都西部智能', goods: 'PLC控制器 S7-1200', qty: '50台', amount: '¥35万', orderTime: '2026-06-23 17:00', deadline: '2026-07-01', status: 'pending', isUrgent: false }
      ],
      filterStatus: '',
      currentPage: 1,
      totalPage: 3,
      statusSteps: ['pending', 'confirmed', 'shipped', 'received', 'settled'],
      charts: {}
    }
  },
  computed: {
    pageList() {
      return [1, 2, 3]
    },
    filteredOrders() {
      if (!this.filterStatus) return this.orderList
      return this.orderList.filter(o => o.status === this.filterStatus)
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initDeliveryChart()
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    Object.values(this.charts).forEach(chart => chart && chart.dispose())
  },
  methods: {
    handleResize() {
      Object.values(this.charts).forEach(chart => chart && chart.resize())
    },
    statusIndex(status) {
      return this.statusSteps.indexOf(status)
    },
    statusText(status) {
      const map = { pending: '待确认', confirmed: '已确认', shipped: '已发货', received: '已收货', settled: '已结算' }
      return map[status] || status
    },
    handleProcess(order) {
      alert(`处理订单：${order.code}\n\n供应商：${order.supplier}\n物品：${order.goods}`)
    },
    handleTrack(item) {
      alert(`订单跟踪：${item.code}\n\n当前状态：${this.statusText(item.status)}\n供应商：${item.supplier}\n要求交期：${item.deadline}`)
    },
    handleExport() {
      alert('正在导出订单明细...')
    },

    // 交付排行迷你柱状图
    initDeliveryChart() {
      const chart = this.$echarts.init(this.$refs.chartDelivery)
      this.charts.delivery = chart
      chart.setOption({
        backgroundColor: 'transparent',
        grid: { left: 10, right: 10, top: 10, bottom: 10 },
        xAxis: { show: false, type: 'value', max: 100 },
        yAxis: { show: false, type: 'category' },
        series: [{
          type: 'bar',
          barWidth: 4,
          data: [98, 96, 94, 91, 88, 85, 82, 79].map((v, i) => ({
            value: v,
            itemStyle: {
              color: i < 3 ? '#00d4ff' : 'rgba(0, 212, 255, 0.3)',
              borderRadius: [2, 2, 2, 2]
            }
          })),
          label: { show: false }
        }]
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.order-page {
  width: 100%;
  min-height: 100%;
  padding-bottom: 20px;
}

/* ==================== 顶部状态看板 ==================== */
.status-board {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.status-item {
  background: rgba(0, 212, 255, 0.04);
  border: 1px solid rgba(0, 212, 255, 0.1);
  border-radius: 10px;
  padding: 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }

  &.blue {
    border-color: rgba(0, 212, 255, 0.2);
    .status-num { color: #00d4ff; }
    .status-fill { background: #00d4ff; }
  }

  &.orange {
    border-color: rgba(240, 160, 48, 0.2);
    .status-num { color: #f0a030; }
    .status-fill { background: #f0a030; }
  }

  &.cyan {
    border-color: rgba(0, 229, 160, 0.2);
    .status-num { color: #00e5a0; }
    .status-fill { background: #00e5a0; }
  }

  &.green {
    border-color: rgba(0, 229, 160, 0.2);
    .status-num { color: #00e5a0; }
    .status-fill { background: #00e5a0; }
  }

  &.red {
    border-color: rgba(255, 107, 107, 0.2);
    .status-num { color: #ff6b6b; }
    .status-fill { background: #ff6b6b; }
  }
}

.status-num {
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 6px;
}

.status-name {
  font-size: 13px;
  color: #8c9bb3;
  margin-bottom: 12px;
}

.status-bar {
  height: 4px;
  background: rgba(140, 155, 179, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.status-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.8s ease;
}

/* ==================== 主体三栏布局 ==================== */
.main-layout {
  display: grid;
  grid-template-columns: 1fr 1.2fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
  height: 480px;
}

.panel {
  background: rgba(0, 212, 255, 0.03);
  border: 1px solid rgba(0, 212, 255, 0.1);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.08);
  flex-shrink: 0;
}

.panel-title {
  font-size: 14px;
  color: #fff;
  font-weight: bold;
  letter-spacing: 1px;
}

.panel-badge {
  padding: 2px 10px;
  border-radius: 10px;
  background: rgba(255, 107, 107, 0.15);
  color: #ff6b6b;
  font-size: 12px;
  font-weight: bold;
}

.panel-body {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.scroll-area {
  overflow-y: auto;
  padding: 12px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 212, 255, 0.05);
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 212, 255, 0.2);
    border-radius: 2px;
  }
}

/* ==================== 左侧：待处理订单卡片 ==================== */
.pending-card {
  background: rgba(0, 212, 255, 0.04);
  border: 1px solid rgba(0, 212, 255, 0.1);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
  transition: all 0.3s;
  cursor: pointer;

  &:hover {
    background: rgba(0, 212, 255, 0.08);
    border-color: rgba(0, 212, 255, 0.25);
  }

  &.urgent {
    border-color: rgba(255, 107, 107, 0.3);
    background: rgba(255, 107, 107, 0.04);

    &:hover {
      border-color: rgba(255, 107, 107, 0.5);
      background: rgba(255, 107, 107, 0.08);
    }

    .pending-code {
      color: #ff6b6b;
    }
  }

  &.low {
    opacity: 0.8;
  }
}

.pending-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.pending-code {
  font-size: 12px;
  color: #00d4ff;
  font-family: 'Courier New', monospace;
}

.pending-time {
  font-size: 11px;
  color: #8c9bb3;
}

.pending-supplier {
  font-size: 13px;
  color: #fff;
  margin-bottom: 4px;
}

.pending-goods {
  font-size: 12px;
  color: #8c9bb3;
  margin-bottom: 10px;
  line-height: 1.4;
}

.pending-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pending-amount {
  font-size: 14px;
  color: #00d4ff;
  font-weight: bold;
}

.pending-btn {
  padding: 4px 12px;
  border-radius: 4px;
  border: 1px solid rgba(0, 212, 255, 0.4);
  background: rgba(0, 212, 255, 0.1);
  color: #00d4ff;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.2);
  }
}

/* ==================== 中间：时间轴 ==================== */
.timeline-wrap {
  padding: 8px 16px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 27px;
    top: 16px;
    bottom: 16px;
    width: 1px;
    background: rgba(0, 212, 255, 0.15);
  }
}

.timeline-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  position: relative;

  .timeline-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: rgba(140, 155, 179, 0.3);
    border: 2px solid #011039;
    flex-shrink: 0;
    margin-top: 4px;
    position: relative;
    z-index: 1;
  }

  .timeline-content {
    flex: 1;
    min-width: 0;
  }

  .timeline-title {
    font-size: 13px;
    color: #fff;
    font-weight: bold;
    margin-bottom: 4px;
  }

  .timeline-desc {
    font-size: 12px;
    color: #8c9bb3;
    margin-bottom: 6px;
    line-height: 1.4;
  }

  .timeline-meta {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: rgba(140, 155, 179, 0.6);

    span:first-child {
      color: #00d4ff;
    }
  }

  &.create .timeline-dot { background: #00d4ff; }
  &.confirm .timeline-dot { background: #00e5a0; }
  &.ship .timeline-dot { background: #f0a030; }
  &.receive .timeline-dot { background: #a855f7; }
  &.settle .timeline-dot { background: #8c9bb3; }
  &.warn .timeline-dot { background: #ff6b6b; animation: blink 1.5s infinite; }
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

/* ==================== 右侧：交付排行 ==================== */
.chart-area {
  height: 60px;
  padding: 0 16px;
}

.rank-list {
  padding: 8px 16px;
}

.rank-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 212, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
}

.rank-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #8c9bb3;
  background: rgba(140, 155, 179, 0.1);
  flex-shrink: 0;

  &.top {
    background: rgba(0, 212, 255, 0.15);
    color: #00d4ff;
    font-weight: bold;
  }
}

.rank-name {
  font-size: 13px;
  color: #c8d4e8;
  width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rank-bar {
  flex: 1;
  height: 6px;
  background: rgba(140, 155, 179, 0.1);
  border-radius: 3px;
  overflow: hidden;
}

.rank-fill {
  height: 100%;
  background: linear-gradient(90deg, #00d4ff, #00e5a0);
  border-radius: 3px;
  transition: width 0.5s ease;
}

.rank-score {
  font-size: 12px;
  color: #00d4ff;
  font-weight: bold;
  width: 28px;
  text-align: right;
}

/* ==================== 底部表格 ==================== */
.bottom-table {
  background: rgba(0, 212, 255, 0.03);
  border: 1px solid rgba(0, 212, 255, 0.1);
  border-radius: 10px;
  padding: 16px;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.table-title {
  font-size: 14px;
  color: #fff;
  font-weight: bold;
  letter-spacing: 1px;
}

.filter-group {
  display: flex;
  gap: 10px;
  align-items: center;
}

.filter-select {
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid rgba(0, 212, 255, 0.2);
  background: rgba(0, 212, 255, 0.05);
  color: #8c9bb3;
  font-size: 12px;
  outline: none;
  cursor: pointer;

  option {
    background: #011039;
    color: #8c9bb3;
  }
}

.action-btn {
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  border: 1px solid rgba(0, 212, 255, 0.4);
  background: rgba(0, 212, 255, 0.08);
  color: #00d4ff;
  transition: all 0.3s;

  &:hover {
    background: rgba(0, 212, 255, 0.15);
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.2);
  }
}

.table-wrap {
  overflow-x: auto;
  margin-bottom: 12px;

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 212, 255, 0.05);
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 212, 255, 0.2);
    border-radius: 2px;
  }
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th, td {
    padding: 12px 14px;
    text-align: left;
    border-bottom: 1px solid rgba(0, 212, 255, 0.08);
    white-space: nowrap;
  }

  th {
    background: rgba(0, 212, 255, 0.06);
    color: #8c9bb3;
    font-size: 12px;
    font-weight: normal;
    letter-spacing: 1px;
  }

  tbody tr {
    transition: all 0.2s;

    &:hover {
      background: rgba(0, 212, 255, 0.04);
    }
  }
}

.code-cell {
  color: #8c9bb3;
  font-family: 'Courier New', monospace;
  font-size: 12px;
}

.goods-cell {
  color: #c8d4e8;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.amount-cell {
  color: #00d4ff;
  font-weight: bold;
}

.urgent {
  color: #ff6b6b;
  font-weight: bold;
}

.status-step {
  display: flex;
  gap: 4px;
  margin-bottom: 4px;
}

.step-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(140, 155, 179, 0.2);

  &.done {
    background: #00e5a0;
  }

  &.current {
    background: #00d4ff;
    box-shadow: 0 0 6px rgba(0, 212, 255, 0.5);
    animation: stepGlow 1.5s infinite;
  }
}

@keyframes stepGlow {
  0%, 100% { box-shadow: 0 0 4px rgba(0, 212, 255, 0.3); }
  50% { box-shadow: 0 0 10px rgba(0, 212, 255, 0.6); }
}

.status-text {
  font-size: 11px;
  color: #8c9bb3;
}

.op-btn {
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  border: none;
  transition: all 0.2s;

  &.primary {
    background: rgba(0, 212, 255, 0.15);
    color: #00d4ff;

    &:hover {
      background: rgba(0, 212, 255, 0.3);
    }
  }
}

/* ==================== 分页 ==================== */
.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 212, 255, 0.08);
}

.footer-info {
  font-size: 12px;
  color: #8c9bb3;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 6px;
}

.page-btn {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: 1px solid rgba(0, 212, 255, 0.2);
  background: transparent;
  color: #8c9bb3;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    border-color: #00d4ff;
    color: #00d4ff;
    background: rgba(0, 212, 255, 0.08);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}

.page-num {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #8c9bb3;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(0, 212, 255, 0.3);
    color: #00d4ff;
  }

  &.active {
    background: rgba(0, 212, 255, 0.15);
    border-color: #00d4ff;
    color: #00d4ff;
  }
}

/* ==================== 响应式 ==================== */
@media (max-width: 1400px) {
  .status-board {
    grid-template-columns: repeat(3, 1fr);
  }

  .main-layout {
    grid-template-columns: 1fr;
    height: auto;
  }

  .center-panel, .right-panel {
    height: 400px;
  }
}

@media (max-width: 768px) {
  .status-board {
    grid-template-columns: repeat(2, 1fr);
  }

  .filter-group {
    flex-wrap: wrap;
  }
}
</style>